import { useState, useEffect, useMemo } from 'react';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import { motion, AnimatePresence } from 'framer-motion';
import { getApiUrl } from '../../lib/api';
import { countryFlag, statusColor } from '../../lib/country-utils';
import TripReportList from '../TripReportList';
import type { CountryProps } from '../../pages/Cannabis';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

interface CountryLaw {
  country: string;
  country_code: string;
  status: string;
  details?: string;
  penalties?: string;
  medical?: string;
  possession_limit?: string;
}

/* ISO 3166-1 alpha-2 → alpha-3 mapping for the TopoJSON (which uses alpha-3) */
const A2_TO_A3: Record<string, string> = {
  AF:'AFG',AL:'ALB',DZ:'DZA',AR:'ARG',AM:'ARM',AU:'AUS',AT:'AUT',AZ:'AZE',
  BS:'BHS',BH:'BHR',BD:'BGD',BB:'BRB',BY:'BLR',BE:'BEL',BZ:'BLZ',BJ:'BEN',
  BT:'BTN',BO:'BOL',BA:'BIH',BW:'BWA',BR:'BRA',BN:'BRN',BG:'BGR',BF:'BFA',
  BI:'BDI',KH:'KHM',CM:'CMR',CA:'CAN',CF:'CAF',TD:'TCD',CL:'CHL',CN:'CHN',
  CO:'COL',KM:'COM',CD:'COD',CG:'COG',CR:'CRI',CI:'CIV',HR:'HRV',CU:'CUB',
  CY:'CYP',CZ:'CZE',DK:'DNK',DJ:'DJI',DO:'DOM',EC:'ECU',EG:'EGY',SV:'SLV',
  GQ:'GNQ',ER:'ERI',EE:'EST',ET:'ETH',FJ:'FJI',FI:'FIN',FR:'FRA',GA:'GAB',
  GM:'GMB',GE:'GEO',DE:'DEU',GH:'GHA',GR:'GRC',GT:'GTM',GN:'GIN',GW:'GNB',
  GY:'GUY',HT:'HTI',HN:'HND',HU:'HUN',IS:'ISL',IN:'IND',ID:'IDN',IR:'IRN',
  IQ:'IRQ',IE:'IRL',IL:'ISR',IT:'ITA',JM:'JAM',JP:'JPN',JO:'JOR',KZ:'KAZ',
  KE:'KEN',KP:'PRK',KR:'KOR',KW:'KWT',KG:'KGZ',LA:'LAO',LV:'LVA',LB:'LBN',
  LS:'LSO',LR:'LBR',LY:'LBY',LT:'LTU',LU:'LUX',MK:'MKD',MG:'MDG',MW:'MWI',
  MY:'MYS',ML:'MLI',MT:'MLT',MR:'MRT',MU:'MUS',MX:'MEX',MD:'MDA',MN:'MNG',
  ME:'MNE',MA:'MAR',MZ:'MOZ',MM:'MMR',NA:'NAM',NP:'NPL',NL:'NLD',NZ:'NZL',
  NI:'NIC',NE:'NER',NG:'NGA',NO:'NOR',OM:'OMN',PK:'PAK',PA:'PAN',PG:'PNG',
  PY:'PRY',PE:'PER',PH:'PHL',PL:'POL',PT:'PRT',QA:'QAT',RO:'ROU',RU:'RUS',
  RW:'RWA',SA:'SAU',SN:'SEN',RS:'SRB',SL:'SLE',SG:'SGP',SK:'SVK',SI:'SVN',
  SO:'SOM',ZA:'ZAF',SS:'SSD',ES:'ESP',LK:'LKA',SD:'SDN',SR:'SUR',SZ:'SWZ',
  SE:'SWE',CH:'CHE',SY:'SYR',TW:'TWN',TJ:'TJK',TZ:'TZA',TH:'THA',TL:'TLS',
  TG:'TGO',TT:'TTO',TN:'TUN',TR:'TUR',TM:'TKM',UG:'UGA',UA:'UKR',AE:'ARE',
  GB:'GBR',US:'USA',UY:'URY',UZ:'UZB',VE:'VEN',VN:'VNM',YE:'YEM',ZM:'ZMB',
  ZW:'ZWE',
};

/* Reverse: alpha-3 → alpha-2 */
const A3_TO_A2: Record<string, string> = {};
for (const [a2, a3] of Object.entries(A2_TO_A3)) A3_TO_A2[a3] = a2;

function statusFill(status?: string | null): string {
  if (!status || typeof status !== 'string') return '#374151';
  const s = status.toLowerCase();
  if (s === 'legal' || s.includes('legal purchase')) return '#22c55e';
  if (s.includes('medical')) return '#3b82f6';
  if (s.includes('tolerated')) return '#f59e0b';
  if (s.includes('decriminalized')) return '#eab308';
  if (s.includes('illegal')) return '#ef4444';
  return '#6b7280';
}

export default function WorldMapTab(_props: CountryProps) {
  const [laws, setLaws] = useState<CountryLaw[]>([]);
  const [selected, setSelected] = useState<CountryLaw | null>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; name: string; status: string } | null>(null);

  useEffect(() => {
    fetch(getApiUrl('/api/country-laws'))
      .then(r => r.json())
      .then(data => setLaws(Array.isArray(data) ? data : data.data ?? []))
      .catch(() => {});
  }, []);

  const lawMap = useMemo(() => {
    const m: Record<string, CountryLaw> = {};
    for (const l of laws) {
      if (l.country_code) {
        const a3 = A2_TO_A3[l.country_code.toUpperCase()];
        if (a3) m[a3] = l;
      }
    }
    return m;
  }, [laws]);

  return (
    <div className="space-y-4">
      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs">
        {[
          { color: '#22c55e', label: 'Legal' },
          { color: '#3b82f6', label: 'Medical' },
          { color: '#eab308', label: 'Decriminalized' },
          { color: '#f59e0b', label: 'Tolerated' },
          { color: '#ef4444', label: 'Illegal' },
          { color: '#6b7280', label: 'Unknown' },
        ].map(l => (
          <div key={l.label} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: l.color }} />
            <span className="text-white/60">{l.label}</span>
          </div>
        ))}
      </div>

      <div className="relative rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <ComposableMap
          projectionConfig={{ scale: 147, center: [0, 20] }}
          style={{ width: '100%', height: 'auto' }}
        >
          <ZoomableGroup>
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map(geo => {
                  const a3 = geo.properties.ISO_A3 || geo.id;
                  const law = lawMap[a3];
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={statusFill(law?.status)}
                      stroke="#1a1a2e"
                      strokeWidth={0.4}
                      style={{
                        default: { outline: 'none', cursor: law ? 'pointer' : 'default' },
                        hover: { outline: 'none', fill: law ? '#d4a574' : '#4b5563' },
                        pressed: { outline: 'none' },
                      }}
                      onClick={() => { if (law) setSelected(law); }}
                      onMouseEnter={(e) => {
                        const { clientX, clientY } = e as unknown as MouseEvent;
                        setTooltip({
                          x: clientX,
                          y: clientY,
                          name: law?.country || geo.properties.name || a3,
                          status: law?.status || 'No data',
                        });
                      }}
                      onMouseLeave={() => setTooltip(null)}
                    />
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>

        {/* Floating tooltip */}
        {tooltip && (
          <div
            className="fixed z-50 px-3 py-1.5 rounded-lg bg-black/90 border border-white/20 text-xs pointer-events-none"
            style={{ left: tooltip.x + 12, top: tooltip.y - 10 }}
          >
            <span className="text-white font-semibold">{tooltip.name}</span>
            <span className="text-white/50 ml-2">{tooltip.status}</span>
          </div>
        )}
      </div>

      {/* Detail sidebar */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="space-y-4"
          >
            {/* Country details */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{countryFlag(selected.country_code)}</span>
                  <h3 className="text-white font-bold text-lg">{selected.country}</h3>
                  <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${statusColor(selected.status)}`}>
                    {selected.status}
                  </span>
                </div>
                <button
                  onClick={() => setSelected(null)}
                  className="text-white/40 hover:text-white/70 text-sm"
                >
                  Close
                </button>
              </div>

              {selected.details && (
                <p className="text-white/60 text-sm leading-relaxed">{selected.details}</p>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                {selected.penalties && (
                  <div className="space-y-1">
                    <span className="text-red-400 font-semibold">Penalties</span>
                    <p className="text-white/50">{selected.penalties}</p>
                  </div>
                )}
                {selected.medical && (
                  <div className="space-y-1">
                    <span className="text-blue-400 font-semibold">Medical</span>
                    <p className="text-white/50">{selected.medical}</p>
                  </div>
                )}
                {selected.possession_limit && (
                  <div className="space-y-1">
                    <span className="text-[#d4a574] font-semibold">Possession Limit</span>
                    <p className="text-white/50">{selected.possession_limit}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Trip Reports */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
              <h4 className="text-white font-bold mb-4">🌎 Trip Reports</h4>
              <TripReportList countryCode={selected.country_code} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
